import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast, Toaster } from "sonner";
import { ShieldCheck, AlertCircle, CheckCircle2, X, Clock, Fingerprint, Upload, Camera, Sparkles, Bot } from "lucide-react";

const STATUS_META = {
  none:     { Icon: Fingerprint,   cls: "border-white/10 bg-white/5 text-white/70",                  title: "KYC Not Started",      body: "Verify your identity to unlock withdrawals. Funds can only be released to a GCash/Maya/Bank account that matches your verified legal name." },
  pending:  { Icon: Clock,          cls: "border-yellow-500/40 bg-yellow-500/10 text-yellow-200",     title: "Under Review",         body: "AI verification flagged this for a human reviewer. You'll be notified here once a ZFD admin completes the check." },
  approved: { Icon: CheckCircle2,   cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",  title: "Verified",             body: "Your identity is verified. You can now request withdrawals — just make sure the payout account name matches your verified legal name." },
  rejected: { Icon: X,              cls: "border-[#DC2626]/40 bg-[#DC2626]/10 text-[#FCA5A5]",        title: "Rejected — Please Resubmit", body: "Your previous submission was rejected. Review the notes below, then submit fresh details." },
};

const ID_TYPES = [
  { v: "national_id",     l: "Philippine National ID (PhilID)" },
  { v: "drivers_license", l: "Driver's License" },
  { v: "passport",        l: "Passport" },
  { v: "umid",            l: "UMID" },
  { v: "sss",             l: "SSS ID" },
  { v: "postal",          l: "Postal ID" },
];

const MAX_KYC_BYTES = 2 * 1024 * 1024;

function ImageUploadField({ label, hint, value, onChange, testid, icon: Icon = Upload }) {
  const ref = useRef(null);
  const handle = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp|heic|heif)$/i.test(file.type)) {
      toast.error("Please upload a PNG, JPG, WEBP, or HEIC image.");
      return;
    }
    if (file.size > MAX_KYC_BYTES) {
      toast.error(`Image must be under 2MB (yours is ${(file.size / 1024 / 1024).toFixed(2)}MB).`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result, file.name);
    reader.readAsDataURL(file);
  };
  return (
    <div>
      <label className="zfd-label flex items-center justify-between">
        <span>{label} <span className="text-[#FCA5A5] font-bold">*</span></span>
        <span className="text-white/40 text-[10px] normal-case tracking-normal">PNG / JPG / WEBP · max 2MB</span>
      </label>
      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/heic,image/heif"
        className="hidden"
        onChange={(e) => handle(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
      {!value ? (
        <button type="button" onClick={() => ref.current?.click()} className="w-full border-2 border-dashed border-white/15 hover:border-[#FBBF24]/60 hover:bg-white/[0.03] rounded-md py-6 px-3 flex flex-col items-center gap-1 transition-colors" data-testid={`${testid}-pick`}>
          <Icon size={20} className="text-[#FBBF24]" />
          <span className="text-xs font-bold text-white/85">{label}</span>
          <span className="text-[10px] text-white/40">{hint}</span>
        </button>
      ) : (
        <div className="relative rounded-md overflow-hidden border border-[#FBBF24]/40" data-testid={`${testid}-preview`}>
          <img src={value} alt={label} className="w-full max-h-56 object-contain bg-black/40" />
          <div className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5 flex items-center justify-end text-xs">
            <button type="button" className="text-[#FCA5A5] hover:text-white font-bold" onClick={() => { onChange("", ""); if (ref.current) ref.current.value = ""; }} data-testid={`${testid}-clear`}>Replace</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AiVerdictCard({ verdict }) {
  if (!verdict) return null;
  const rec = (verdict.recommendation || "REVIEW").toUpperCase();
  const palette = rec === "PASS" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : rec === "FAIL" ? "border-[#DC2626]/40 bg-[#DC2626]/10 text-[#FCA5A5]"
                : "border-yellow-500/40 bg-yellow-500/10 text-yellow-200";
  const matchPct = Math.round(((verdict.match_confidence ?? 0) * 100));
  const authPct = Math.round(((verdict.id_authenticity_score ?? 0) * 100));
  return (
    <div className={`glass-card border ${palette} p-4 flex gap-3`} data-testid="kyc-ai-verdict">
      <Bot className="shrink-0 mt-0.5" />
      <div className="text-xs leading-relaxed flex-1">
        <div className="font-bold uppercase tracking-widest text-[11px]">AI Verdict: {rec}</div>
        <div className="opacity-85 mt-1">{verdict.rationale}</div>
        <div className="mt-2 flex flex-wrap gap-3 text-[11px]">
          <span>Face match: <strong>{matchPct}%</strong></span>
          <span>ID authenticity: <strong>{authPct}%</strong></span>
          {verdict.anomalies?.length > 0 && <span>Flags: {verdict.anomalies.join(", ")}</span>}
        </div>
      </div>
    </div>
  );
}

export default function Kyc() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_legal_name: user?.full_name || "",
    id_type: "national_id",
    id_number: "",
    id_image: "",
    selfie_with_id: "",
    declaration: false,
  });

  const load = async () => {
    const { data } = await api.get("/kyc/mine");
    setState(data);
    if (data.submission?.full_legal_name) setForm((f) => ({ ...f, full_legal_name: data.submission.full_legal_name }));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.id_image || !form.selfie_with_id) {
      toast.error("Both your ID photo and a selfie with the ID are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/kyc", form);
      const rec = data?.ai_verdict?.recommendation?.toUpperCase() || "REVIEW";
      if (data.status === "approved") toast.success("AI verified your identity — withdrawals unlocked!");
      else if (data.status === "rejected") toast.error("AI rejected this submission. Please review the notes and try again.");
      else toast.message(`AI verdict: ${rec}. Sent to admin for human review.`);
      setForm({ ...form, declaration: false });
      await Promise.all([load(), refresh()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!state) return <div className="text-white/55">Loading…</div>;

  const meta = STATUS_META[state.status] || STATUS_META.none;
  const canSubmit = state.status === "none" || state.status === "rejected";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="kyc-root">
      <Toaster position="top-right" richColors />
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Identity Verification</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">KYC Compliance</h1>
        <p className="text-white/60 mt-1 text-sm max-w-2xl">
          Required by anti-fraud policy before any withdrawal. AI checks your ID photo against a live selfie — funds are only ever released to an account that <strong>matches your verified legal name</strong>.
        </p>
      </div>

      <div className={`glass-card border ${meta.cls} p-5 flex gap-4`} data-testid={`kyc-status-${state.status}`}>
        <meta.Icon size={28} className="shrink-0 mt-1" />
        <div className="flex-1">
          <div className="font-display text-lg font-bold">{meta.title}</div>
          <p className="text-sm mt-1 opacity-90">{meta.body}</p>
          {state.status === "approved" && (
            <div className="mt-3 text-xs">
              Verified legal name: <span className="font-mono-num text-[#FBBF24]" data-testid="kyc-approved-name">{state.approved_name}</span>
            </div>
          )}
          {state.status === "rejected" && state.submission?.reject_reason && (
            <div className="mt-3 text-xs">
              <span className="uppercase tracking-widest text-[10px] opacity-70">Why this was rejected:</span><br/>
              <span data-testid="kyc-reject-reason">{state.submission.reject_reason}</span>
            </div>
          )}
        </div>
      </div>

      {state.submission?.ai_verdict && <AiVerdictCard verdict={state.submission.ai_verdict} />}

      {canSubmit && (
        <form onSubmit={submit} className="glass-card gold-border p-6 space-y-5">
          <div className="flex items-center gap-2 text-[#FBBF24]"><ShieldCheck /> <span className="font-display text-lg font-bold">{state.status === "rejected" ? "Resubmit Verification" : "Submit Verification"}</span></div>

          <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/30 rounded-md p-3 text-xs text-[#FBBF24] flex gap-2">
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <div>
              <strong>Use your REAL name exactly as it appears on your ID.</strong> This will become your locked legal name.
              Your GCash, Maya, and bank account names must all match this exact name — no nicknames, no third-party names —
              or withdrawals will be rejected.
            </div>
          </div>

          <div>
            <label className="zfd-label">Full Legal Name (as printed on ID)</label>
            <input required minLength={3} className="zfd-input" value={form.full_legal_name}
              onChange={(e) => setForm({ ...form, full_legal_name: e.target.value })}
              data-testid="kyc-name" placeholder="e.g. Juan Dela Cruz" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="zfd-label">ID Type</label>
              <select className="zfd-input" value={form.id_type} onChange={(e) => setForm({ ...form, id_type: e.target.value })} data-testid="kyc-id-type">
                {ID_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <label className="zfd-label">ID Number</label>
              <input required className="zfd-input" value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="kyc-id-number" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageUploadField
              label="ID Photo"
              hint="Clear photo of the front of your government ID"
              value={form.id_image}
              onChange={(v) => setForm({ ...form, id_image: v })}
              testid="kyc-id-image"
              icon={Upload}
            />
            <ImageUploadField
              label="Selfie holding your ID"
              hint="Your face + the SAME ID, clearly visible"
              value={form.selfie_with_id}
              onChange={(v) => setForm({ ...form, selfie_with_id: v })}
              testid="kyc-selfie"
              icon={Camera}
            />
          </div>

          <div className="bg-[#60A5FA]/10 border border-[#60A5FA]/30 rounded-md p-3 text-xs text-[#93C5FD] flex gap-2">
            <Sparkles size={16} className="shrink-0 mt-0.5" />
            <div>
              <strong>AI-powered verification:</strong> our model compares the face on your ID with the face in your selfie.
              High match = instant approval. Low match = automatic rejection. Anything in between is sent to a human reviewer.
            </div>
          </div>

          <label className="flex items-start gap-3 text-xs text-white/75 cursor-pointer pt-1" data-testid="kyc-declaration-wrap">
            <input type="checkbox" checked={form.declaration}
              onChange={(e) => setForm({ ...form, declaration: e.target.checked })}
              className="mt-0.5" data-testid="kyc-declaration" />
            <span>
              I attest that the information and images above match my government-issued ID exactly, and that I am the person in the selfie. I understand that any
              mismatch with my GCash/Maya/Bank account name will cause withdrawals to be rejected, and that misrepresentation
              may result in account suspension.
            </span>
          </label>

          <button disabled={busy || !form.declaration || !form.id_image || !form.selfie_with_id} type="submit" className="btn-deploy w-full disabled:opacity-50" data-testid="kyc-submit">
            {busy ? "Running AI verification…" : "Submit for Verification"}
          </button>
        </form>
      )}
    </div>
  );
}
